import { Building2, User } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getSupplierAddConfig,
  getSupplierById,
  getSupplierLabels,
  type SupplierDetailData,
} from '@/api/suppliers';
import { StatusChip, type SupplierStatus } from '@/components/status-chip';
import { SupplierDetailContent } from '@/components/supplier-detail-content';
import { Badge } from '@/components/ui/badge';
import { useDocumentTitle } from '@/contexts/document-title-context';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';

export const SupplierDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useKeycloakAuth();

  const [supplier, setSupplier] = useState<SupplierDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [labels, setLabels] = useState(getSupplierLabels('supplier'));
  const [externalRefLabel, setExternalRefLabel] = useState<string>('External Ref');

  const fetchSupplier = useCallback(async () => {
    if (!id) {
      setIsLoading(false);
      setNotFound(true);
      return;
    }
    setError(null);
    setNotFound(false);
    try {
      const data = await getSupplierById(id);
      setSupplier(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load beneficiary';
      if (/not found/i.test(message)) {
        setNotFound(true);
      } else {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSupplier();
  }, [fetchSupplier]);

  useEffect(() => {
    const partyId = user?.partyId;
    if (!partyId) return;
    getSupplierAddConfig(partyId)
      .then((config) => {
        setLabels(getSupplierLabels(config.displayName ?? 'supplier'));
        setExternalRefLabel(config.externalRefDisplayName?.trim() || 'External Ref');
      })
      .catch(() => {});
  }, [user?.partyId]);

  // Hoisted above the early returns so the document title can reuse the name the
  // heading shows; hooks cannot be called conditionally.
  const personName = supplier
    ? `${supplier.first_name || ''} ${supplier.last_name || ''}`.trim()
    : '';
  const displayName = supplier ? personName || supplier.business_name || 'Unknown' : null;

  useDocumentTitle(displayName);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <p className="text-sm text-muted-foreground">Loading {labels.singular.toLowerCase()}…</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <p className="text-sm text-muted-foreground">{labels.singular} not found.</p>
        <Link to="/suppliers" className="text-sm underline underline-offset-4">
          Back to {labels.plural}
        </Link>
      </div>
    );
  }

  if (error || !supplier) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive text-sm">
          {error ?? `Failed to load ${labels.singular.toLowerCase()}.`}
        </div>
        <Link to="/suppliers" className="text-sm underline underline-offset-4">
          Back to {labels.plural}
        </Link>
      </div>
    );
  }

  const isIndividual =
    supplier.type === 'INDIVIDUAL' || (!supplier.business_name && Boolean(personName));

  return (
    <div className="flex flex-col gap-4 px-4 py-4 md:gap-5 md:py-6 lg:px-6">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
        <header className="flex flex-col gap-4">
          <nav className="text-xs text-muted-foreground">
            <Link to="/suppliers" className="hover:text-foreground">
              {labels.plural}
            </Link>
            <span className="mx-1.5">/</span>
            <span className="text-foreground">{displayName}</span>
          </nav>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2.5">
                {isIndividual ? (
                  <User className="h-6 w-6 text-muted-foreground shrink-0" />
                ) : (
                  <Building2 className="h-6 w-6 text-muted-foreground shrink-0" />
                )}
                <span className="truncate">{displayName}</span>
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {supplier.client_name ? `${supplier.client_name} · ` : ''}
                {isIndividual ? 'Individual' : labels.singular}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {supplier.status && (
                <StatusChip status={supplier.status as SupplierStatus} type="supplier" />
              )}
              <Badge variant="outline">{isIndividual ? 'Individual' : labels.singular}</Badge>
            </div>
          </div>
        </header>

        <SupplierDetailContent
          supplier={supplier}
          labels={labels}
          externalRefLabel={externalRefLabel}
          onSupplierChange={fetchSupplier}
        />
      </div>
    </div>
  );
};
